import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { clearAuth, getAdmin, getToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetHeader } from '@/components/ui/sheet';
import { ADMIN } from '@/constants/testIds';
import { ClubMark } from '@/components/PublicShell';
import { LayoutDashboard, Users, MessageSquare, Settings, ShieldCheck, LogOut, Menu } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, testId: ADMIN.navDashboard, end: true },
  { to: '/admin/animals', label: 'Animals', icon: Users, testId: ADMIN.navAnimals },
  { to: '/admin/enquiries', label: 'Enquiries', icon: MessageSquare, testId: ADMIN.navEnquiries },
  { to: '/admin/settings', label: 'Settings', icon: Settings, testId: ADMIN.navSettings },
  { to: '/admin/admins', label: 'Admins', icon: ShieldCheck, testId: ADMIN.navAdmins },
];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const listener = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
    // mq/listener are created inside this effect; setIsDesktop is a stable
    // setState from React. There is nothing external to depend on — this
    // effect must only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return isDesktop;
}

function NavList({ onNavigate, testIdSuffix = '' }) {
  return (
    <nav data-testid={ADMIN.nav} className="space-y-1">
      {NAV_ITEMS.map(({ to, label, icon: Icon, testId, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={`${testId}${testIdSuffix}`}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-3 rounded-lg text-[15px] font-bold transition-colors min-h-[48px] ${
              isActive
                ? 'bg-[#1E3A2F] text-white'
                : 'text-[#22322B] hover:bg-[#F7F8F7]'
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const nav = useNavigate();
  const admin = getAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!getToken()) nav('/admin/login');
  }, [nav]);

  const logout = () => {
    clearAuth();
    nav('/admin/login');
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar — desktop only */}
        {isDesktop && (
          <aside className="flex flex-col h-screen sticky top-0 border-r border-border bg-white">
            <div className="px-5 py-5 border-b border-border">
              <ClubMark />
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <NavList />
            </div>
            <div className="p-3 border-t border-border">
              <div className="px-3 py-2">
                <p className="text-sm font-bold truncate">{admin?.name || admin?.email}</p>
                <p className="text-xs text-muted-foreground truncate">{admin?.role}</p>
              </div>
              <Button data-testid={ADMIN.navLogout} variant="outline" size="sm" onClick={logout} className="w-full rounded-lg btn-sm h-10">
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </aside>
        )}

        {/* Main */}
        <div className="min-h-screen flex flex-col">
          {/* Mobile top bar */}
          {!isDesktop && (
            <header className="sticky top-0 z-40 border-b border-border bg-white">
              <div className="h-14 px-4 flex items-center justify-between">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button data-testid={ADMIN.navOpenButton} variant="ghost" size="icon" className="h-11 w-11">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-72 p-0 bg-white flex flex-col">
                    <SheetHeader className="px-5 py-5 border-b border-border">
                      <SheetTitle className="sr-only">Stable Office menu</SheetTitle>
                      <SheetDescription className="sr-only">Navigate the Stable Office admin.</SheetDescription>
                      <ClubMark />
                    </SheetHeader>
                    <div className="p-3 flex-1 overflow-y-auto">
                      <NavList onNavigate={() => setMobileOpen(false)} />
                    </div>
                    <div className="p-3 border-t border-border">
                      <p className="px-3 text-sm font-bold truncate">{admin?.name || admin?.email}</p>
                      <p className="px-3 text-xs text-muted-foreground truncate mb-2">{admin?.role}</p>
                      <Button data-testid={ADMIN.navLogout} variant="outline" size="sm" onClick={logout} className="w-full rounded-lg btn-sm h-10">
                        <LogOut className="h-4 w-4 mr-2" />Sign out
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                <ClubMark />
                <div className="w-11" />
              </div>
            </header>
          )}

          <main className="flex-1 p-4 sm:p-6 lg:p-10">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
